import { IListColumnConfig } from '@datachain/ui-sdk/common';
import { Map } from 'immutable';

import dayjs from '../configs/dayjs';
import {
	ColumnCompositeFilterExpr,
	CompositeFilterExpr,
	DxFilterField,
	DxOperations,
	DxSortField,
	isColumnCompositeFilter,
	isCompositeFilter,
	isSimpleFilter,
	SimpleFilterExp,
} from '../dx-types';
import { StoredColumn } from '../services/dx-lists.storage';
import { Operator, OperatorUtil } from './operator-mapping.util';

export class DxGridUtil {
	public static parseFilters<RowAttr = Record<string, unknown>>(
		filter:
			| SimpleFilterExp<RowAttr>
			| ColumnCompositeFilterExpr<RowAttr>
			| CompositeFilterExpr<RowAttr>,
		extracted: Array<DxFilterField<RowAttr>> = []
	): Array<DxFilterField<RowAttr>> {
		const out = extracted;
		if (isSimpleFilter<RowAttr>(filter)) {
			const [column, op, val] = filter;
			if (!Array.isArray(val) && val !== undefined && val !== null) {
				if (typeof val === 'string' && val.trim() !== '') {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					out.push({
						field: column,
						operator: op,
						value: val,
					});
				} else if (typeof val !== 'string') {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					out.push({
						field: column,
						operator: op,
						value: val,
					});
				}
			} else {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				out.push({
					field: column,
					operator: op,
					value: val,
				});
			}
		} else if (isColumnCompositeFilter<RowAttr>(filter)) {
			const [columnLeft, , columnRight] = filter;
			DxGridUtil.parseFilters(columnLeft, out);
			DxGridUtil.parseFilters(columnRight, out);
		} else if (isCompositeFilter<RowAttr>(filter)) {
			const [compositeLeft, , compositeRight] = filter;
			DxGridUtil.parseFilters(compositeLeft, out);
			DxGridUtil.parseFilters(compositeRight, out);
		}
		return out;
	}

	// to delete it
	public static generateSortFieldsV1<T>(
		columns: Map<string, IListColumnConfig<T>>,
		action: string,
		sortOrder: 'asc' | 'desc'
	): Array<DxSortField> {
		const columnNumber = action.match(/columns\[(\d+)\]/);
		const index = columnNumber ? parseInt(columnNumber[1], 10) : null;
		const [field] =
			columns.findEntry((column) => column.visibleIndex === index) ?? [];
		const sortedFields: Array<DxSortField> = [];
		if (field) {
			sortedFields.push({
				selector: field,
				order: sortOrder,
			});
		}
		return sortedFields;
	}

	public static generateSortFields(
		columns: Array<Partial<StoredColumn>>
	): Array<DxSortField> {
		return columns
			.filter((c) => c.sortIndex !== undefined)
			.sort((a, b) => (a.sortIndex ?? -1) - (b.sortIndex ?? -1))
			.map<DxSortField | undefined>((c) => {
				if (c.dataField && c.sortOrder) {
					return {
						selector: c.dataField,
						order: c.sortOrder,
					};
				}
				return undefined;
			})
			.filter((sf): sf is DxSortField => sf !== undefined);
	}

	public static generateDateFilter(
		field: string,
		operator: Operator,
		selectedDate: Date | Array<Date>
	): Record<string, string> {
		const result: Record<string, string> = {};

		if (operator === Operator.Between) {
			const [selectedStartDate, selectedEndDate] = selectedDate as Array<Date>;

			const startDate = dayjs(selectedStartDate as Date);
			let endDate = startDate.add(1, 'year');

			if (selectedEndDate) {
				endDate = dayjs(selectedEndDate as Date).add(1, 'd');
			}

			result[`${field}.${Operator.GreaterThanOrEqual}`] =
				startDate.toISOString();
			result[`${field}.${Operator.LessThan}`] = endDate.toISOString();
			return result;
		}

		const startDate = dayjs(selectedDate as Date);
		const endDate = startDate.add(1, 'day');
		switch (operator) {
			case Operator.Equals: {
				result[`${field}.${Operator.GreaterThanOrEqual}`] =
					startDate.toISOString();
				result[`${field}.${Operator.LessThan}`] = endDate.toISOString();
				break;
			}
			case Operator.LessThan: {
				result[`${field}.${Operator.LessThan}`] = startDate.toISOString();
				break;
			}
			case Operator.LessThanOrEqual: {
				result[`${field}.${Operator.LessThanOrEqual}`] = endDate.toISOString();
				break;
			}
			case Operator.GreaterThan: {
				result[`${field}.${Operator.GreaterThan}`] = endDate.toISOString();
				break;
			}
			case Operator.GreaterThanOrEqual: {
				result[`${field}.${Operator.GreaterThanOrEqual}`] =
					startDate.toISOString();
				break;
			}
			default: {
				throw new Error(`Unrecognized Operator: ${operator}`);
			}
		}

		return result;
	}

	public static generateFilterFieldPayload(
		filterList: Array<DxFilterField>,
		fieldMapping: Record<string, string>
	): Record<
		string,
		string | boolean | Array<string | number | boolean | Date>
	> {
		let resultFilter: Record<
			string,
			string | boolean | Array<string | number | boolean | Date>
		> = {};

		filterList.map((filter) => {
			const { value, operator } = filter;

			if (operator === DxOperations.Between && Array.isArray(value)) {
				const resultDateFilter = this.handleDate(filter, fieldMapping);
				resultFilter = {
					...resultFilter,
					...resultDateFilter,
				};
			} else if (operator === DxOperations.In && Array.isArray(value)) {
				const key = `${fieldMapping[filter.field]}.${Operator.In}`;
				resultFilter[key] = value;
			} else if (value instanceof Date) {
				const resultDateFilter = this.handleDate(filter, fieldMapping);
				resultFilter = {
					...resultFilter,
					...resultDateFilter,
				};
			} else if (typeof value === 'boolean') {
				const key = `${fieldMapping[filter.field]}.${Operator.Specified}`;
				resultFilter[key] = !value;
			} else if (typeof value === 'string') {
				const { key, value } = this.handleNonDateField(filter, fieldMapping);
				resultFilter[key] = value;
			}
		});

		return resultFilter;
	}

	public static generateSortFieldPayload(
		sortList: Array<DxSortField>,
		fieldMapping: Record<string, string>
	): Array<string> {
		return sortList.map((item) => {
			const { order } = item;
			return `${fieldMapping[item.selector]},${order}`;
		});
	}

	private static handleDate(
		filter: DxFilterField,
		fieldMapping: Record<string, string>
	): Record<string, string> {
		const { field, operator, value } = filter;
		const mappedField = fieldMapping[field];
		const mappingOperator = OperatorUtil.getOperator(operator as DxOperations);

		const _fieldValue = value as Date | Array<Date>;
		return DxGridUtil.generateDateFilter(
			mappedField,
			mappingOperator,
			_fieldValue
		);
	}

	private static handleNonDateField(
		filter: DxFilterField,
		fieldMapping: Record<string, string>
	): {
		key: string;
		value: string;
	} {
		const { field, operator, value } = filter;
		const _fieldValue = value as string;
		const mappedField = fieldMapping[field];
		const mappingOperator = OperatorUtil.getOperator(operator as DxOperations);

		return {
			key: `${mappedField}.${mappingOperator}`,
			value: _fieldValue,
		};
	}
}
